
import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, X, Film, Volume2, VolumeX, Share2, MoreVertical, ChevronDown, Search, ArrowLeft, Menu, Settings, Hammer, Zap, Clock, Trophy } from 'lucide-react';
import { LoadingBar } from '../Layout/LoadingBar';
import { FullPageLoader } from '../Common/FullPageLoader';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { VideoItem } from '../../types';
import { fetchShortsFromSupabase } from '../../services/youtubeVideoService';
import { useYouTubeAPI } from '../../hooks/useYouTubePlayer';
import { XlevenLogo } from '../Common/XlevenLogo';
import { SearchOverlay } from '../Home/SearchOverlay';
import { useVideo } from '../../context/VideoContext';
import SEO from '../Common/SEO';
import { EmptyState } from '../Common/EmptyState';
import { t } from '../../i18n';

interface ShortsViewProps {
  onOpenSidebar: () => void;
}

const SimpleYouTubePlayer: React.FC<{ videoId: string; onClose: () => void }> = ({ videoId, onClose }) => (
    <div className="absolute inset-0 z-20 bg-black flex items-center justify-center">
        <div className="w-full h-full">
            <iframe 
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&controls=1&loop=1&playlist=${videoId}&rel=0`} 
                className="w-full h-full" 
                allow="autoplay; encrypted-media; picture-in-picture" 
                allowFullScreen
                title="Shorts Video" 
            />
        </div>
        <button 
            onClick={(e) => { e.stopPropagation(); onClose(); }} 
            className="absolute top-24 right-6 bg-black/40 backdrop-blur-md p-2.5 rounded-full z-[100] text-white md:hover:bg-black/60 transition-colors pointer-events-auto"
        >
            <X size={20} />
        </button>
    </div>
);

const YouTubePlayer: React.FC<{ videoId: string; isMuted: boolean; isPaused: boolean; onClose: () => void; onToggleMute: () => void; onTogglePlay: () => void; onError?: () => void }> = ({ videoId, isMuted, isPaused, onClose, onToggleMute, onTogglePlay, onError }) => {
  const apiReady = useYouTubeAPI();
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(false);

  useEffect(() => {
    if (!apiReady || !containerRef.current || !window.YT) return;
    setIsReady(false); // Reset ready state when videoId changes
    
    try {
        playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: videoId, height: '100%', width: '100%',
        playerVars: { 
            autoplay: 1, 
            mute: isMuted ? 1 : 0, 
            playsinline: 1, 
            controls: 0, 
            modestbranding: 1, 
            loop: 1, 
            playlist: videoId, 
            rel: 0, 
            fs: 0,
            origin: window.location.origin
        },
        events: {
            onReady: (e: any) => { 
                // 음소거 상태로 시작하여 자동재생 보장
                e.target.mute();
                const playPromise = e.target.playVideo();
                
                // unMute logic only if parent says so, but for shorts we default to mute
                if (!isMuted) {
                    setTimeout(() => {
                        try {
                            e.target.unMute();
                            e.target.setVolume(100);
                            
                            setTimeout(() => {
                                if (playerRef.current && playerRef.current.getPlayerState() !== 1) {
                                    setShowPlayOverlay(true);
                                }
                            }, 1000);
                        } catch (err) {
                            setShowPlayOverlay(true);
                        }
                    }, 500);
                }
            },
            onStateChange: (e: any) => {
                // YT.PlayerState.PLAYING is 1
                if (e.data === 1) {
                    setIsReady(true);
                    setShowPlayOverlay(false);
                }
                // YT.PlayerState.PAUSED is 2
                if (e.data === 2 && !isPaused) {
                    // If it was supposed to be playing but got paused (likely by browser policy)
                    setShowPlayOverlay(true);
                }
            },
            onError: (e: any) => {
                console.error("YT Player Error:", e.data);
                setHasError(true);
                if (onError) onError();
            }
        }});
    } catch (err) { 
        console.error("YT Player Init Error:", err);
        setHasError(true);
        if (onError) onError();
    }
    return () => { try { playerRef.current?.destroy(); } catch(e){} };
  }, [apiReady, videoId]);

  useEffect(() => {
      if (playerRef.current) {
          if (isPaused) {
              playerRef.current.pauseVideo();
          } else {
              playerRef.current.playVideo();
          }
      }
  }, [isPaused]);

  useEffect(() => {
      if (playerRef.current?.mute) isMuted ? playerRef.current.mute() : playerRef.current.unMute();
  }, [isMuted]);

    if (hasError) return <SimpleYouTubePlayer videoId={videoId} onClose={onClose} />;

    return (
        <div className={`absolute inset-0 z-20 bg-black flex items-center justify-center transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
            <div ref={containerRef} className="w-full h-full scale-[1.01]" />
            
            {/* Explicit Play Button for Autoplay Policy bypass */}
            {showPlayOverlay && (
                <div className="absolute inset-0 flex items-center justify-center z-[65] bg-black/40 backdrop-blur-[2px] hidden md:flex">
                    <button 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            if (playerRef.current) {
                                // playerRef.current.unMute();
                                playerRef.current.playVideo();
                            }
                            // setShowPlayOverlay(false);
                        }}
                        className="w-24 h-24 bg-rose-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse group"
                    >
                        <Play size={48} className="text-white ml-2 fill-white" />
                    </button>
                </div>
            )}

            {isReady && (
                <div className="absolute top-0 left-0 right-0 p-6 flex justify-end z-[70]">
                    <button 
                        onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation(); 
                            onToggleMute(); 
                        }} 
                        className="pointer-events-auto bg-white/10 backdrop-blur-md p-2.5 rounded-full text-white md:hover:bg-white/20 transition-colors"
                    >
                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                </div>
            )}
    </div>
  );
};

/**
 * 점검 중 페이지 컴포넌트
 */
const MaintenanceView: React.FC<{ lang: string }> = ({ lang }) => {
    const [timeLeft, setTimeLeft] = useState<string>('');

    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const target = new Date();
            target.setUTCHours(24, 0, 0, 0); // 다음 UTC 00:00
            
            const diff = target.getTime() - now.getTime();
            if (diff <= 0) {
                setTimeLeft('00:00:00');
                return;
            }

            const h = Math.floor(diff / (1000 * 60 * 60)).toString().padStart(2, '0');
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
            const s = Math.floor((diff % (1000 * 60)) / 1000).toString().padStart(2, '0');
            setTimeLeft(`${h}:${m}:${s}`);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center bg-black text-white relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 animate-pulse"><Zap size={100} className="text-rose-500" /></div>
                <div className="absolute bottom-1/4 right-1/4 animate-bounce"><Film size={150} className="text-indigo-500" /></div>
            </div>

            <div className="relative z-10 max-w-md animate-in fade-in zoom-in duration-700">
                <div className="relative w-32 h-32 mx-auto mb-10">
                    {/* Spinning Gears */}
                    <Settings className="absolute -top-2 -right-2 text-rose-500 animate-spin" size={48} style={{ animationDuration: '4s' }} />
                    <Hammer className="absolute -bottom-2 -left-2 text-indigo-500 animate-bounce" size={40} />
                    
                    {/* Pulsing Center Icon */}
                    <div className="w-full h-full bg-white/5 backdrop-blur-xl rounded-[40px] border border-white/10 flex items-center justify-center shadow-2xl">
                        <Film size={56} className="text-white animate-pulse" />
                    </div>
                </div>

                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter mb-4 text-rose-500">
                    {t(lang as any, 'maintenanceTitle')}
                </h2>
                <p className="text-sm md:text-base font-medium text-slate-400 leading-relaxed mb-10 px-4 whitespace-pre-wrap">
                    {t(lang as any, 'maintenanceDesc')}
                </p>

                {/* Countdown Box */}
                <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-md">
                    <div className="text-4xl md:text-5xl font-black tabular-nums tracking-tighter text-white drop-shadow-lg">
                        {timeLeft || '00:00:00'}
                    </div>
                    <p className="mt-4 text-[11px] font-black text-rose-500 uppercase tracking-widest">
                        {t(lang as any, 'maintenanceUntil')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const ShortsView: React.FC<ShortsViewProps> = ({ onOpenSidebar }) => {
    const { lang } = useLanguage();
    const { activeVideo, setActiveVideo } = useVideo();
    const [videos, setVideos] = useState<VideoItem[]>([]);
    const [displayVideos, setDisplayVideos] = useState<VideoItem[]>([]);
    const [invalidVideoIds, setInvalidVideoIds] = useState<Set<string>>(new Set());
    const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [isMuted, setIsMuted] = useState(true); // 기본 음소거 설정
    const [isPaused, setIsPaused] = useState(false);
    const [isMaintenance, setIsMaintenance] = useState(false);
    
    // Search State
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        loadVideos(searchQuery);
    }, [lang, searchQuery]);

    // Filter invalid videos
    useEffect(() => {
        if (videos.length > 0) {
            // Filter out videos that are known to be invalid
            const validVideos = videos.filter(v => !invalidVideoIds.has(v.videoId));
            setDisplayVideos(validVideos);
        } else {
            setDisplayVideos([]);
        }
    }, [videos, invalidVideoIds]);

    // Pause PIP if a Short starts playing
    useEffect(() => {
        if (activeVideoIndex !== null && activeVideo && !activeVideo.isPaused) {
            setActiveVideo({ ...activeVideo, isPaused: true });
        }
    }, [activeVideoIndex]);

    // Intersection Observer to handle auto-play on scroll
    useEffect(() => {
        const options = {
            root: containerRef.current,
            threshold: 0.7 // 70% of the video must be visible to trigger auto-play
        };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const index = parseInt(entry.target.getAttribute('data-index') || '0', 10);
                setActiveVideoIndex(index);
                // 영상 전환 시 자동 재생 보장을 위해 일시정지 상태 해제 및 음소거 유지
                setIsPaused(false);
                setIsMuted(true);
            }
        });
    }, options);

        const currentItems = document.querySelectorAll('.shorts-item');
        currentItems.forEach((item) => observer.observe(item));

        return () => {
            currentItems.forEach((item) => observer.unobserve(item));
        };
    }, [displayVideos, loading]);

    const loadVideos = (query: string) => {
        // 검색어가 있을 때만 전체 로딩바를 표시하여 깜빡임 방지
        if (query) setLoading(true);
        setIsMaintenance(false);
        fetchShortsFromSupabase(query)
            .then(setVideos)
            .finally(() => setLoading(false));
    };

    const handleSearchSubmit = (query: string) => {
        setSearchQuery(query);
    };

    const clearSearch = () => {
        setSearchQuery('');
    };

    const loadingText = searchQuery 
        ? `${t(lang, 'searchingFor')} "${searchQuery}"...` 
        : t(lang, '');

    // 점검 중 UI 표시
    if (isMaintenance) {
        return (
            <div className="bg-black h-screen w-full relative">
                <MaintenanceView lang={lang} />
            </div>
        );
    }

    return (
        <div className="bg-black h-[calc(100vh-64px)] md:h-screen w-full relative flex flex-col md:pb-0 overflow-x-hidden">
            <SEO 
                title={t(lang, 'shortsSeoTitle')}
                description={t(lang, 'shortsSeoDesc')}
            />
            <LoadingBar isLoading={loading} />
            <SearchOverlay 
                isOpen={isSearchOpen} 
                onClose={() => setIsSearchOpen(false)} 
                onNavigate={() => {}} 
                onSearch={handleSearchSubmit}
                type="shorts"
            />

            <div className="absolute top-0 left-0 right-0 z-[100] p-4 flex justify-between items-center bg-gradient-to-b from-black/90 via-black/50 to-transparent pointer-events-none md:hidden h-14">
                <div className="pointer-events-auto flex items-center gap-3">
                    {!searchQuery && (
                        <div className="flex items-center gap-3">
                            <XlevenLogo theme="dark" />
                        </div>
                    )}
                    {searchQuery && (
                        <div className="flex items-center gap-2">
                             <button onClick={clearSearch} className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full text-white border border-white/20">
                                 <ArrowLeft size={16} />
                             </button>
                             <span className="text-white font-bold text-lg truncate max-w-[150px]">"{searchQuery}"</span>
                        </div>
                    )}
                </div>
            </div>

            <div ref={containerRef} className="flex-1 overflow-y-auto overflow-x-hidden snap-y snap-mandatory no-scrollbar relative h-full w-full">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 border-4 border-rose-500/20 border-t-rose-500 rounded-full animate-spin"></div>
                            <span className="text-white font-bold">{loadingText}</span>
                        </div>
                    </div>
                ) : displayVideos.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center bg-slate-900">
                        <EmptyState 
                            lang={lang}
                            title={t(lang, 'shortsPreparingTitle')}
                            description={t(lang, 'shortsPreparingDesc')}
                            icon={Film}
                            actionLabel={t(lang, 'goBack')}
                            onAction={() => window.navigateTo('/')}
                        />
                    </div>
                ) : (
                    displayVideos.map((video, index) => (
                        <div key={`${video.id}-${index}`} data-index={index} className="shorts-item w-full h-full snap-start snap-always relative bg-gray-900 border-b border-gray-800 flex-shrink-0 overflow-hidden">
                            {activeVideoIndex === index ? (
                                <div className="absolute inset-0 z-20">
                                    <YouTubePlayer 
                                        videoId={video.videoId} 
                                        isMuted={isMuted} 
                                        isPaused={isPaused}
                                        onClose={() => setActiveVideoIndex(null)} 
                                        onToggleMute={() => setIsMuted(!isMuted)} 
                                        onTogglePlay={() => setIsPaused(!isPaused)}
                                        onError={() => {
                                            setInvalidVideoIds(prev => new Set(prev).add(video.videoId));
                                        }}
                                    />
                                </div>
                            ) : null}
                            
                            <div className="w-full h-full relative cursor-pointer group" onClick={() => setActiveVideoIndex(index)}>
                                <img 
                                    src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`} 
                                    alt="" 
                                    className="w-full h-full object-cover opacity-90 md:group-hover:opacity-100" 
                                    onError={(e) => e.currentTarget.src = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`} 
                                />
                                
                            </div>

                            <div className="absolute bottom-0 left-0 right-0 pb-6 px-6 bg-gradient-to-t from-black/95 via-black/60 to-transparent z-[80] pointer-events-none">
                                <div className="flex justify-between items-end">
                                    <div className="flex-1">
                                        <div 
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                if (video.channel_url) {
                                                    window.open(video.channel_url, '_blank');
                                                } else if (video.channelId) {
                                                    window.open(`https://www.youtube.com/channel/${video.channelId}`, '_blank');
                                                }
                                            }} 
                                            className="flex items-center gap-3 mb-3 pointer-events-auto cursor-pointer w-fit"
                                        >
                                            <div className="w-10 h-10 rounded-full border-2 border-black overflow-hidden bg-black flex-shrink-0">
                                                <img src={video.channelImage || 'https://ui-avatars.com/api/?background=random'} alt="" className="w-full h-full object-cover"/>
                                            </div>
                                            <div>
                                                <span className="text-white font-bold text-sm block">{video.channel}</span>
                                                <button className="text-rose-500 text-[10px] font-black md:hover:underline">SUBSCRIBE</button>
                                            </div>
                                        </div>
                                        <h3 className="text-white font-black text-lg line-clamp-2 pointer-events-none">{video.title}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
